// Copyright (c) Docugami, Inc. All rights reserved.

import { createEntityAdapter, createSlice, EntityState, nanoid, PayloadAction } from "@reduxjs/toolkit";
import { Recipe } from "@reduxjs/toolkit/dist/query/core/buildThunks";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { chatErrorResponseMessage } from "components/chat/ChatHelpers";
import { Config } from "config";
import { OrganizationSignalRConnectionInstance, waitForSignalRConnection } from "networking/OrganizationSignalRConnection";
import type { AppDispatch, AppState } from "store";
import { createDgBaseQuery } from "store/baseQuery";
import { Logger, LoggerCategory } from "utils/Logger";
import { ChatJobStatus, ChatMessagePair, ChatOptions, CreateFeedbackRequest, CreateFeedbackResponse, DocSetChatResponse, isSystemError, isSystemMessage, parseChatOptionsResponse, RawTextChatMessage, SendStreamingChatMessageResponse, StreamedChatMessageResponse, StreamingChatMessageRequest, SystemChatMessage, SystemErrorMessage, SystemTextChatMessage, TextChatMessage, UpdateFeedbackRequest } from "./chatApi";

interface MessageFeedback {
    id: string;
    feedback: boolean;
    messageId: string;
    comment?: string;
}

export interface ChatSuggestion {
    id: string;
    content: string;
}

export type ChatMessageModel = TextChatMessage | SystemChatMessage;
export interface DocSetChatState {
    docSetId: string;
    options: ChatOptions;
    suggestions: EntityState<ChatSuggestion>;
    chatHistory: EntityState<ChatMessageModel>;
}

export const chatHistoryAdapter = createEntityAdapter<ChatMessageModel>({
    selectId: x => x.id,
    // Keep chat history sorted in reverse chronological order
    sortComparer: (a, b) => b.timestamp - a.timestamp
});

export const {
    selectAll: selectAllChatHistory
} = chatHistoryAdapter.getSelectors();

const suggestionsAdapter = createEntityAdapter<ChatSuggestion>({
    selectId: x => x.id,
});

export const {
    selectAll: selectAllChatSuggestions
} = suggestionsAdapter.getSelectors();

const chatBaseQuery = createDgBaseQuery(`${Config.chatUrl}/chat`);
export const chatApi = createApi({
    reducerPath: 'chatApi',
    tagTypes: ['DocSetChat'],
    baseQuery: chatBaseQuery,
    endpoints: builder => ({
        initializeDocSetChat: builder.query<DocSetChatState, ChatOptions>({
            query: request => ({
                url: `/${request.docsetId}/initialize`,
                method: 'POST',
                body: request
            }),
            transformResponse: (response: DocSetChatResponse, meta, request) => {
                return ({
                    docSetId: request.docsetId,
                    options: request,
                    chatHistory: chatHistoryAdapter.setAll(chatHistoryAdapter.getInitialState(), (response?.chatHistory || []).map(x => ({
                        ...x,
                        id: x.messageId,
                        timestamp: Date.parse(x.timestamp),
                        sessionId: undefined
                    }))),
                    suggestions: suggestionsAdapter.setAll(suggestionsAdapter.getInitialState(), (response?.suggestions || []).map(x => ({ id: nanoid(), content: x })))
                })
            },
            onCacheEntryAdded: async (arg, api) => {
                await api.cacheDataLoaded;
                const state = api.getCacheEntry().data;

                function handleUpdate(payload: StreamedChatMessageResponse) {
                    if (payload.options.docsetId !== state?.docSetId) {
                        return;
                    }

                    api.dispatch(handleChatStreamUpdate(payload));
                }

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("chatStreamUpdated", handleUpdate);
                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("chatStreamUpdated", handleUpdate);
                }
            }
        }),
        sendChatMessageWithStreaming: builder.mutation<TextChatMessage, StreamingChatMessageRequest>({
            query: request => ({
                url: `${request.options.docsetId}/message/streaming`,
                method: 'POST',
                body: request
            }),
            transformResponse: (response: SendStreamingChatMessageResponse<RawTextChatMessage>, meta, request) => {
                return {
                    ...response.message,
                    id: response.message.messageId,
                    timestamp: Date.parse(response.message.timestamp),
                    sessionId: response.sessionId
                }
            },
            onQueryStarted: async (arg, api) => {
                // For now, the client is managing cache updates for chat history
                // Add the user message to history when the send message query starts
                const chatOptions = arg.options;

                // Use the messageId from the request (generated by caller) so it matches what the server uses for SignalR parentId
                const messageId = arg.messageId ?? nanoid();
                const patchResult = api.dispatch(
                    chatApi.util.updateQueryData(
                        'initializeDocSetChat',
                        chatOptions,
                        x => {
                            const newMessage: TextChatMessage = {
                                id: messageId,
                                text: arg.message,
                                timestamp: new Date().getTime(),
                                isFinal: true,
                                sessionId: arg.sessionId
                            }
                            chatHistoryAdapter.addOne(x.chatHistory, newMessage);
                        }));

                try {
                    const response = await api.queryFulfilled;

                    // The session id has changed
                    // For now, just silently update the single session id for this docset
                    if (!!response.data?.sessionId && response.data.sessionId !== arg.sessionId) {
                        api.dispatch(setChatSession({ docSetId: arg.options.docsetId,  sessionId: response.data.sessionId }));

                        // Also update the user message's sessionId so SignalR responses can match it
                        api.dispatch(
                            chatApi.util.updateQueryData(
                                'initializeDocSetChat',
                                chatOptions,
                                x => {
                                    chatHistoryAdapter.updateOne(x.chatHistory, {
                                        id: messageId,
                                        changes: { sessionId: response.data.sessionId }
                                    });
                                }));
                    }
                } catch {
                    // We failed to get a response, so roll back the user message cache update
                    patchResult.undo();
                }
            }
        }),
        addFeedback: builder.mutation<CreateFeedbackResponse, CreateFeedbackRequest>({
            query: request => ({
                url: `create-feedback`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                const chatOptions: ChatOptions = {
                    docsetId: arg.docsetId,
                    reportId: arg.reportId,
                    extractionProvider: arg.extractionProvider,
                    extractionModel: arg.extractionModel
                };

                const patchResult = api.dispatch(
                    chatApi.util.updateQueryData(
                        'initializeDocSetChat',
                        chatOptions,
                        updateMessageFeedbackRecipe({
                            id: nanoid(),
                            messageId: arg.feedback.messageId,
                            feedback: arg.feedback.feedback,
                            comment: arg.feedback.comment
                        })));

                try {
                    const response = await api.queryFulfilled;

                    if (!!response.data) {
                        api.dispatch(
                            chatApi.util.updateQueryData(
                                'initializeDocSetChat',
                                chatOptions,
                                updateMessageFeedbackRecipe({
                                    id: response.data.feedbackId,
                                    messageId: response.data.messageId,
                                    feedback: response.data.feedback,
                                    comment: response.data.comment
                                })));
                    }
                } catch {
                    patchResult.undo();
                }
            }
        }),
        updateFeedback: builder.mutation<void, UpdateFeedbackRequest>({
            query: request => ({
                url: `update-feedback`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                const chatOptions: ChatOptions = {
                    docsetId: arg.docsetId,
                    reportId: arg.reportId,
                    extractionProvider: arg.extractionProvider,
                    extractionModel: arg.extractionModel
                };

                const patchResult = api.dispatch(
                    chatApi.util.updateQueryData(
                        'initializeDocSetChat',
                        chatOptions,
                        updateMessageFeedbackRecipe({
                            id: arg.feedbackId,
                            messageId: arg.feedback.messageId,
                            feedback: arg.feedback.feedback,
                            comment: arg.feedback.comment
                        })));

                try {
                    await api.queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        }),
        getJobStatus: builder.query<ChatJobStatus, string>({
            query: jobId => ({
                url: `jobs/${jobId}`,
                method: 'GET'
            })
        }),
        cancelJob: builder.mutation<void, string>({
            query: jobId => ({
                url: `jobs/${jobId}/cancel`,
                method: 'POST'
            })
        })
    })
});

export const {
    useLazyInitializeDocSetChatQuery,
    useInitializeDocSetChatQuery,
    useSendChatMessageWithStreamingMutation,
    useAddFeedbackMutation,
    useUpdateFeedbackMutation,
    useLazyGetJobStatusQuery,
    useGetJobStatusQuery,
    useCancelJobMutation
} = chatApi


export const updateSuggestionsThunk = (suggestionId: string, chatOptions: ChatOptions) => async (dispatch: AppDispatch, getState: () => AppState) =>
    dispatch(chatApi.util.updateQueryData('initializeDocSetChat', chatOptions, x => { suggestionsAdapter.removeOne(x.suggestions, suggestionId) }));

const updateMessageFeedbackRecipe = (input: MessageFeedback): Recipe<DocSetChatState> => x => {
    chatHistoryAdapter.updateOne(x.chatHistory, {
        id: input.messageId,
        changes: {
            feedback: { ...input }
        }
    })
}

interface AddFeedbackInput {
    messageId: string;
    feedback: boolean;
    comment?: string;
}

export function giveChatFeedbackThunk(input: AddFeedbackInput, options: ChatOptions) {
    return async (dispatch: AppDispatch, getState: () => AppState) => {
        const messageToUpdate = chatApi.endpoints.initializeDocSetChat.select(options)(getState()).data?.chatHistory.entities[input.messageId];
        if (!messageToUpdate || !isSystemMessage(messageToUpdate) || isSystemError(messageToUpdate)) {
            Logger.LogWarning(LoggerCategory.Chat, `Could not find system chat message to give feedback to: ${messageToUpdate?.id}`);
            return;
        }

        if (!!messageToUpdate.feedback?.id) {
            const updateFeedbackRequest: UpdateFeedbackRequest = {
                ...options,
                feedbackId: messageToUpdate.feedback.id,
                feedback: {
                    messageId: input.messageId,
                    feedback: input.feedback,
                    feedbackId: messageToUpdate.feedback.id,
                    sessionId: messageToUpdate.sessionId ?? '',
                    comment: input.comment ?? '',
                    senderId: ''
                }
            };
            await dispatch(chatApi.endpoints.updateFeedback.initiate(updateFeedbackRequest));
        } else {
            const createFeedbackRequest: CreateFeedbackRequest = {
                ...options,
                feedback: {
                    messageId: input.messageId,
                    feedback: input.feedback,
                    feedbackId: '',
                    sessionId: messageToUpdate.sessionId ?? '',
                    comment: input.comment ?? '',
                    senderId: ''
                }
            };
            await dispatch(chatApi.endpoints.addFeedback.initiate(createFeedbackRequest));
        }
    }
}

// Gets the latest chat message pairs between user and system from the chat history
const numMessagesForContext = 3;
export const getRequestChatHistoryContext = (chatOptions: ChatOptions, sessionId?: string) => (dispatch: AppDispatch, getState: () => AppState): ChatMessagePair[] => {
    const history = chatApi.endpoints.initializeDocSetChat.select(chatOptions)(getState()).data?.chatHistory;
    const historyContext: ChatMessagePair[] = [];

    if (!!history) {
        // The chat history is stored in reverse chronological order,
        // so we can just iterate from beginning to end to get the latest messages
        for (const id of history.ids) {
            const message = history.entities[id];
            // Push valid, non-empty system responses and their relevant parent user messages into the history context
            if (!!message?.text && isSystemMessage(message)) {
                const userMessage = history.entities[message.parentId];
                if (!!userMessage && userMessage.sessionId === sessionId) {
                    historyContext.push({ humanMessage: userMessage.text, aiMessage: message.text });
                }
            }

            if (historyContext.length >= numMessagesForContext) {
                break;
            }
        }
    }

    return historyContext.reverse();
}

export function createErrorResponse(parentId: string, message: string, error: string, sessionId?: string): SystemErrorMessage {
    return ({
        id: nanoid(),
        parentId: parentId,
        timestamp: Date.now(),
        text: message,
        error: error,
        isFinal: true,
        sessionId: sessionId
    })
}

export function handleChatStreamUpdate(response: StreamedChatMessageResponse) {
    return (dispatch: AppDispatch, getState: () => AppState) => {
        const isError = 'error' in response;
        const parentId = !isError ? response.message.parentId : response.parentId;

        const arg: ChatOptions = parseChatOptionsResponse(response.options);

        dispatch(chatApi.util.updateQueryData("initializeDocSetChat", arg, x => {
            // Drop the incoming message if there is no associated parent message
            const parentMessage = x.chatHistory.entities[parentId];
            if (!parentMessage) {
                Logger.LogWarning(LoggerCategory.Chat, `Could not find parent ${parentId} in history for streamed response update. Dropping update.`);
                return;
            }

            // Find any existing response message for the parent user message
            const existingResponse = selectAllChatHistory(x.chatHistory).find(x => isSystemMessage(x) && x.parentId === parentId) as SystemTextChatMessage | SystemErrorMessage;
            // Drop the incoming message if there is an error response for this parent message
            if (!!existingResponse && isSystemError(existingResponse)) {
                Logger.LogInformation(LoggerCategory.Chat, `Message ${parentId} already has an error response. Dropping update.`);
                return;
            }

            if (isError) {
                // The incoming response is an error
                Logger.LogError(LoggerCategory.Chat, `Received streamed chat response error for chat context: ${JSON.stringify(arg)}`, response.error);
                if (!!existingResponse) {
                    chatHistoryAdapter.updateOne(x.chatHistory, {
                        id: existingResponse.id,
                        changes: { ...createErrorResponse(response.parentId, chatErrorResponseMessage, response.error, parentMessage.sessionId) }
                    });
                } else {
                    chatHistoryAdapter.addOne(x.chatHistory, createErrorResponse(response.parentId, '', response.error, parentMessage.sessionId));
                }
                return;
            }

            const incomingResponse = response.message;
            if (parentMessage.sessionId !== response.sessionId) {
                Logger.LogWarning(LoggerCategory.Chat, `Session mismatch between parent message ${parentMessage.sessionId} and incoming response ${response.sessionId}. Dropping update.`);
                return;
            }
    
            if (!existingResponse) {
                // THis is the first response received for the parent message
                chatHistoryAdapter.addOne(x.chatHistory, {
                    id: !!incomingResponse.messageId ? incomingResponse.messageId : nanoid(),
                    text: incomingResponse.text,
                    timestamp: Date.parse(incomingResponse.timestamp),
                    citations: incomingResponse.citations,
                    parentId: incomingResponse.parentId,
                    isFinal: incomingResponse.isFinal,
                    sessionId: response.sessionId,
                    jobId: incomingResponse.jobId
                });
                return;
            }

            // We follow the following rules while handling subsequent chat stream updates:
            //  - The default treatment for a streamed update is as a text delta. We append the new text chunk to any existing response.
            //  - Streamed updates with no message id, or new ids, are treated as upserts, so any existing response is replaced with the new message.
            if (existingResponse.id === incomingResponse.messageId) {
                // Append text
                chatHistoryAdapter.updateOne(x.chatHistory, {
                    id: existingResponse.id,
                    changes: {
                        text: existingResponse.text + incomingResponse.text,
                        citations: [...existingResponse.citations, ...incomingResponse.citations],
                        isFinal: incomingResponse.isFinal,
                        jobId: incomingResponse.jobId ?? existingResponse.jobId
                    }
                })
            } else {
                // Replace text
                chatHistoryAdapter.updateOne(x.chatHistory, {
                    id: existingResponse.id,
                    changes: {
                        id: !!incomingResponse.messageId ? incomingResponse.messageId : nanoid(),
                        text: incomingResponse.text,
                        citations: incomingResponse.citations,
                        isFinal: incomingResponse.isFinal,
                        jobId: incomingResponse.jobId ?? existingResponse.jobId
                    }
                })
            }
        }))
    }
}

interface ChatSliceState {
    chatSessions: { [id: string]: string };
}

export const chatSlice = createSlice({
    name: 'chatSlice',
    initialState: {
        chatSessions: {}
    } as ChatSliceState,
    reducers: {
        setChatSession(state, action: PayloadAction<ChatSessionPayload>) {
            if (action.payload.sessionId === undefined) {
                delete state.chatSessions[action.payload.docSetId];
            }
            else {
                state.chatSessions[action.payload.docSetId] = action.payload.sessionId;
            }
        }
    }
});

export const { setChatSession } = chatSlice.actions;

interface ChatSessionPayload {
    docSetId: string;
    sessionId: string | undefined;
}